<template>
  <div class="card shadow-sm border-0">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
        <router-link to="/" class="btn btn-outline-secondary">
          <i class="bi bi-arrow-left"></i> Volver al inicio
        </router-link>
        <input
          type="text"
          class="form-control w-auto"
          placeholder="Buscar cédula..."
          v-model="filtroCedula"
        />
      </div>

      <div v-if="usuariosFiltrados.length > 0" class="table-responsive">
        <table class="table align-middle table-hover">
          <thead class="table-light">
            <tr style="font-family: 'ColgateReady-Regular';">
              <th>Cédula</th>
              <th>Nombre completo</th>
              <th>Teléfono</th>
              <th>Correo</th>
              <th>Ciudad</th>
              <th>Kit reclamado</th>
              <th>Fecha entrega</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="usuario in paginatedUsuarios"
              :key="usuario._id"
              style="font-family: 'ColgateReady-Light';"
            >
              <td>{{ usuario.cedula }}</td>
              <td>{{ usuario.nombres }} {{ usuario.apellidos }}</td>
              <td>{{ usuario.telefono }}</td>
              <td>{{ usuario.email }}</td>
              <td>{{ usuario.ciudad }}</td>
              <td>
                <span
                  class="badge rounded-pill"
                  :class="getEstado(usuario)[0] === 'entregado' ? 'bg-success' : 'bg-danger'"
                >
                  {{ getEstado(usuario)[0] === 'entregado' ? 'Sí' : 'No' }}
                </span>
              </td>
              <td>
                {{
                  getEstado(usuario)[0] === 'entregado'
                    ? new Date(getEstado(usuario)[1]).toLocaleString()
                    : '-'
                }}
              </td>
              <td class="text-center">
                <button class="btn btn-sm btn-outline-primary" @click="abrirModal(usuario)">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary ms-1" @click="verDetalles(usuario)">
                  <i class="bi bi-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Paginación -->
        <div class="d-flex justify-content-center align-items-center gap-3 mt-4" v-if="totalPages > 1">
          <button
            class="btn btn-sm"
            :class="currentPage === 1 ? 'btn-secondary' : 'btn-outline-secondary'"
            :disabled="currentPage === 1"
            @click="cambiarPagina(currentPage - 1)"
          >
            Anterior
          </button>
        
          <span class="fw-semibold" style="font-family: 'ColgateReady-Regular';">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
        
          <button
            class="btn btn-sm text-white"
            :class="currentPage === totalPages ? 'btn-secondary' : 'btn-danger'"
            :disabled="currentPage === totalPages"
            @click="cambiarPagina(currentPage + 1)"
          >
            Siguiente
          </button>
        </div>
      </div>

      <div v-else class="alert alert-warning text-center">
        No hay registros disponibles para este día.
      </div>
    </div>
  </div>
</template>

<script setup>
import Swal from 'sweetalert2';
import api from '../services/api';
import { defineProps, defineEmits, ref, computed } from 'vue';

const props = defineProps(['usuarios', 'dia']);
const emit = defineEmits(['actualizar']);

const filtroCedula = ref('');
const currentPage = ref(1);
const perPage = 15;

const getEstado = (usuario) => {
  const kit = usuario.kits_entregados?.[props.dia] || {};
  return [kit.estado || 'pendiente', kit.fecha_entrega];
};

const abrirModal = (usuario) => {
  Swal.fire({
    title: 'Cambiar estado',
    input: 'select',
    inputOptions: {
      pendiente: 'Pendiente',
      entregado: 'Entregado'
    },
    inputValue: getEstado(usuario)[0],
    showCancelButton: true,
    confirmButtonText: 'Guardar',
    cancelButtonText: 'Cancelar',
    preConfirm: (nuevoEstado) => {
      return api
        .put(`/usuarios/${usuario._id}`, {
          dia: props.dia,
          estado: nuevoEstado
        })
        .then(() => {
          Swal.fire('Actualizado', '', 'success');
          emit('actualizar');
        })
        .catch((error) => {
          console.error('Error al actualizar el estado:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo actualizar el estado. Intenta nuevamente.',
            confirmButtonText: 'Cerrar'
          });
        });
    }
  });
};

const verDetalles = (usuario) => {
  Swal.fire({
    title: 'Detalles del usuario',
    html: `
      <div style="text-align: left">
        <b>Cédula:</b> ${usuario.cedula}<br/>
        <b>Nombre:</b> ${usuario.nombres} ${usuario.apellidos}<br/>
        <b>Teléfono:</b> ${usuario.telefono}<br/>
        <b>Email:</b> ${usuario.email}<br/>
        <b>Ciudad:</b> ${usuario.ciudad}<br/>
        <b>Dirección:</b> ${usuario.direccion || '-'}<br/>
        <b>Especialidad:</b> ${usuario.especialidad || '-'}<br/>
        <b>Kit Día:</b> ${props.dia}<br/>
        <b>Estado:</b> ${getEstado(usuario)[0]}<br/>
        <b>Fecha de entrega:</b> ${
          getEstado(usuario)[1]
            ? new Date(getEstado(usuario)[1]).toLocaleString()
            : '-'
        }
      </div>
    `,
    confirmButtonText: 'Cerrar'
  });
};

const usuariosFiltrados = computed(() =>
  Array.isArray(props.usuarios)
    ? props.usuarios.filter((u) =>
        u.cedula?.toLowerCase().includes(filtroCedula.value.toLowerCase())
      )
    : []
);

const paginatedUsuarios = computed(() => {
  const start = (currentPage.value - 1) * perPage;
  return usuariosFiltrados.value.slice(start, start + perPage);
});

const totalPages = computed(() => Math.ceil(usuariosFiltrados.value.length / perPage));

const cambiarPagina = (nuevaPagina) => {
  if (nuevaPagina >= 1 && nuevaPagina <= totalPages.value) {
    currentPage.value = nuevaPagina;
  }
};
</script>

<style scoped>
.card {
  border-radius: 1rem;
}

.table {
  font-size: 0.95rem;
}

.badge {
  font-size: 0.85rem;
  padding: 0.4em 0.7em;
}
</style>
